import moment from "moment-timezone";
import { getCanonicalTimezone, getTimezoneOptions } from "@helpers/timezone";

const MINUTES_PER_HOUR = 60;
// Shown in the list even when the browser does not name it among its zones
const UTC_ZONE = "UTC";

const pad = (value) => String(value).padStart(2, "0");

/**
 * Current offset of a zone from UTC as in PO: "UTC+2", "UTC-3:30".
 *
 * @param {string} timezone - IANA zone
 * @returns {string}
 */
export const formatUtcOffset = (timezone) => {
  const offsetMinutes = moment.tz(timezone).utcOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const hours = Math.floor(Math.abs(offsetMinutes) / MINUTES_PER_HOUR);
  const minutes = Math.abs(offsetMinutes) % MINUTES_PER_HOUR;

  return minutes ? `UTC${sign}${hours}:${pad(minutes)}` : `UTC${sign}${hours}`;
};

/** @param {string} timezone - "America/New_York" → "America/New York" */
export const formatTimezoneName = (timezone) => timezone.replace(/_/g, " ");

/** IANA zone of the browser, the «Your location» button. */
export const getBrowserTimezone = () =>
  getCanonicalTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || UTC_ZONE);

/**
 * Zones to choose from, the same list as on the profile page
 * (getTimezoneOptions), sorted by the current offset, then by name.
 * The current zone is always there; old names (Kiev) still find the new ones.
 *
 * @param {string} currentTimezone
 * @returns {{ timezone: string, name: string, offset: string, searchText: string }[]}
 */
export const buildTimezoneOptions = (currentTimezone) => {
  return getTimezoneOptions([UTC_ZONE, currentTimezone])
    .map(({ timezone, aliases }) => ({
      timezone,
      aliases,
      name: formatTimezoneName(timezone),
      offset: formatUtcOffset(timezone),
      offsetMinutes: moment.tz(timezone).utcOffset(),
    }))
    .sort((first, second) =>
      first.offsetMinutes !== second.offsetMinutes
        ? first.offsetMinutes - second.offsetMinutes
        : first.name.localeCompare(second.name),
    )
    .map(({ timezone, aliases, name, offset }) => ({
      timezone,
      name,
      offset,
      searchText: [name, timezone, ...aliases.map(formatTimezoneName), offset]
        .join(" ")
        .toLowerCase(),
    }));
};
